import React, { useMemo, useState, useEffect } from "react";
import { NORWEGIAN_INVENTORY } from "../data/norwegianInventory";

import ArticulationSideView from "../components/ArticulationSideView";
import ArticulationModal from "../components/ArticulationModal";

import { ENGLISH_PACK } from "../data/l1packs/english";
import { RUSSIAN_PACK } from "../data/l1packs/russian";

import { PHONETICS_GLOSSARY, GLOSSARY_KEYS } from "../data/phoneticsGlossary";

// IMPORTANT: adjust this path to where you actually store norwegianTargets.js
// Your message shows: // src/poses/norwegianTargets.js
import { NO_TARGETS } from "../data/l2packs/norwegianTargets"; 

const L1_PACKS = [ENGLISH_PACK, RUSSIAN_PACK];

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* -------------------------- glossary tokenization -------------------------- */
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function tokenizeWithGlossary(text) {
  if (!text) return [{ type: "text", value: "" }];

  const pattern = GLOSSARY_KEYS.map(escapeRegExp).join("|");
  const re = new RegExp(
    `(^|[\\s"“”'‘’()\\[\\]{}<>.,:;!?/\\\\+=*~^%$#@|…–—±])(${pattern})(?=$|[\\s"“”'‘’()\\[\\]{}<>.,:;!?/\\\\+=*~^%$#@|…–—±])`,
    "gi"
  );

  const out = [];
  let last = 0;
  let m;

  while ((m = re.exec(text)) !== null) {
    const full = m[0];
    const left = m[1] || "";
    const term = m[2];
    const start = m.index;
    const end = start + full.length;

    if (start > last) out.push({ type: "text", value: text.slice(last, start) });
    if (left) out.push({ type: "text", value: left });
    out.push({ type: "term", value: term });

    last = end;
  }

  if (last < text.length) out.push({ type: "text", value: text.slice(last) });
  return out;
}

function normalizeKey(term) {
  return String(term || "").toLowerCase();
}

/* ------------------------------ overlap helpers --------------------------- */
function normalizeOverlap(o) {
  if (o === "–" || o === "—") return "-";
  return o || "-";
}
function overlapLabel(o) {
  const x = normalizeOverlap(o);
  if (x === "+") return "Easy (close match)";
  if (x === "~") return "Medium (partial overlap)";
  return "Hard (no analogy)";
}
function overlapTileClass(o) {
  const x = normalizeOverlap(o);
  if (x === "+") return "bg-green-100 text-green-900 border-green-200 hover:bg-green-200";
  if (x === "~") return "bg-yellow-100 text-yellow-900 border-yellow-200 hover:bg-yellow-200";
  return "bg-red-100 text-red-900 border-red-200 hover:bg-red-200";
}
function overlapSelectedRingClass(o) {
  const x = normalizeOverlap(o);
  if (x === "+") return "ring-green-500";
  if (x === "~") return "ring-yellow-500";
  return "ring-red-500";
}

function difficultyLabel(d) {
  if (d === "new") return "NEW sound";
  if (d === "watch") return "Watch out";
  return "OK";
}
function difficultyClass(d) {
  if (d === "new") return "bg-red-50 text-red-800 border-red-200";
  if (d === "watch") return "bg-yellow-50 text-yellow-800 border-yellow-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

/* -------------------------------- components ------------------------------ */
function Legend() {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <span className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-slate-700">
        <span className="h-2 w-2 rounded-full bg-green-500" />
        Easy
      </span>
      <span className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-slate-700">
        <span className="h-2 w-2 rounded-full bg-yellow-500" />
        Medium
      </span>
      <span className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-slate-700">
        <span className="h-2 w-2 rounded-full bg-red-500" />
        Hard
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function SoundTile({ ipa, overlap, selected, onClick }) {
  const x = normalizeOverlap(overlap);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "rounded-xl border px-3 py-2 text-sm font-semibold transition active:scale-[0.99]",
        overlapTileClass(x),
        selected ? cx("ring-2 ring-offset-2 opacity-100", overlapSelectedRingClass(x)) : "opacity-90 hover:opacity-100"
      )}
      title={overlapLabel(x)}
    >
      /{ipa}/
    </button>
  );
}

/* -------------------------- glossary popover inline ------------------------ */
function KeywordPopover({ termKey, anchorRect, onClose }) {
  const entry = PHONETICS_GLOSSARY[termKey];
  if (!entry || !anchorRect) return null;

  const style = {
    position: "fixed",
    left: Math.min(window.innerWidth - 360, Math.max(12, anchorRect.left)),
    top: Math.min(window.innerHeight - 520, Math.max(12, anchorRect.bottom + 8)),
    width: 360,
    zIndex: 50,
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
      <div style={style} className="z-50 rounded-2xl border bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 p-4">
          <div>
            <div className="text-sm font-semibold text-slate-900">{entry.title}</div>
            <div className="mt-1 text-sm text-slate-700">{entry.body}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
        <div className="border-t px-4 py-3 text-xs text-slate-500">
          Tip: copy the tongue/lip position, then say the Norwegian example again.
        </div>
      </div>
    </>
  );
}

function KeywordText({ text }) {
  const [openKey, setOpenKey] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const tokens = useMemo(() => tokenizeWithGlossary(text), [text]);

  const close = () => {
    setOpenKey(null);
    setAnchorRect(null);
  };

  return (
    <span className="leading-relaxed">
      {tokens.map((t, i) => {
        if (t.type === "text") return <span key={i}>{t.value}</span>;

        const key = normalizeKey(t.value);
        const entry = PHONETICS_GLOSSARY[key];
        if (!entry) return <span key={i}>{t.value}</span>;

        return (
          <button
            key={i}
            type="button"
            className="mx-0.5 inline-flex items-center rounded-full border bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setAnchorRect(rect);
              setOpenKey(key);
            }}
            title="Click for explanation"
          >
            {t.value}
          </button>
        );
      })}

      <KeywordPopover termKey={openKey} anchorRect={anchorRect} onClose={close} />
    </span>
  );
}

/* ------------------------------ UI: chips --------------------------------- */
function ChipRow({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {items.map((k) => (
        <span
          key={k}
          className="inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700"
        >
          {k}
        </span>
      ))}
    </div>
  );
}

/* --------------------------------- details -------------------------------- */
const LS_COMMENT_MODE_KEY = "sounds_comment_mode"; // "easy" | "detailed"

function Details({ selected, l1Pack }) {
  const [commentMode, setCommentMode] = useState("easy");
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_COMMENT_MODE_KEY);
      if (saved === "easy" || saved === "detailed") setCommentMode(saved);
    } catch {
      // ignore
    }
  }, []);

  const setMode = (m) => {
    setCommentMode(m);
    try {
      localStorage.setItem(LS_COMMENT_MODE_KEY, m);
    } catch {
      // ignore
    }
  };

  if (!selected) {
    return (
      <div className="rounded-2xl border bg-slate-50 p-5 text-sm text-slate-600">
        Click a Norwegian sound tile to see the L1 analogy and notes.
      </div>
    );
  }

  const { ipa, type, no } = selected;
  const info = l1Pack?.byIpa?.[ipa] ?? null;

  const overlap = normalizeOverlap(info?.overlap ?? "-");
  const l1 = (info?.l1 || []).filter(Boolean);

  const easy = (info?.easyComment || "").trim();
  const detailed = (info?.userComment || info?.comment || "").trim();
  const displayComment = commentMode === "easy" ? (easy || detailed) : (detailed || easy);

  const difficulty = info?.difficulty || null;
  const keywords = Array.isArray(info?.keywords) ? info.keywords.filter(Boolean) : [];

  const badgeClass = overlapTileClass(overlap)
    .replace("hover:bg-green-200", "")
    .replace("hover:bg-yellow-200", "")
    .replace("hover:bg-red-200", "");

  // Use Norwegian targets (your new render-based ones for p/b)
  const targetIpa = info?.targetIpa || ipa;
  const noTarget = NO_TARGETS?.[targetIpa] || null;

  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs text-slate-500">Sound</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">/{ipa}/</div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className={cx("inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium", badgeClass)}>
              {overlapLabel(overlap)}
            </div>

            {difficulty ? (
              <div className={cx("inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold", difficultyClass(difficulty))}>
                {difficultyLabel(difficulty)}
              </div>
            ) : null}
          </div>

          <div className="mt-2 text-sm text-slate-600">
            Type: <span className="font-medium">{type}</span>
          </div>

          <ChipRow items={keywords} />
        </div>

        <div className="text-right text-xs text-slate-500">
          <div>
            L1: <span className="font-medium text-slate-700">{l1Pack?.meta?.name}</span>
          </div>

          <div className="mt-3 inline-flex rounded-xl border bg-white p-1">
            <button
              type="button"
              onClick={() => setMode("easy")}
              className={cx(
                "rounded-lg px-2.5 py-1 text-[11px] font-semibold transition",
                commentMode === "easy" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
              )}
            >
              Easy
            </button>
            <button
              type="button"
              onClick={() => setMode("detailed")}
              className={cx(
                "rounded-lg px-2.5 py-1 text-[11px] font-semibold transition",
                commentMode === "detailed" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
              )}
            >
              Detailed
            </button>
          </div>
        </div>
      </div>

      {/* Diagram + modal */}
      <div className="mt-4">
        <ArticulationSideView
          target={noTarget}
          label="Articulation"
          onOpenModal={() => setModalOpen(true)}
          enableModalClick={true}
        />

        <ArticulationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          target={noTarget}
        />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-xl bg-slate-50 p-4">
          <div className="text-xs font-semibold text-slate-700">Norwegian examples</div>
          <div className="mt-2 text-sm text-slate-900">
            {no?.length ? (
              <ul className="list-disc pl-5">
                {no.map((w, i) => (
                  <li key={`${w}-${i}`}>{w}</li>
                ))}
              </ul>
            ) : (
              <div className="text-slate-500">—</div>
            )}
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 p-4">
          <div className="text-xs font-semibold text-slate-700">Closest L1 analogy</div>
          <div className="mt-2 text-sm text-slate-900">
            {l1.length ? (
              <ul className="list-disc pl-5">
                {l1.map((w, i) => (
                  <li key={`${w}-${i}`}>{w}</li>
                ))}
              </ul>
            ) : (
              <div className="font-medium text-slate-700">No analogy</div>
            )}
          </div>
        </div>
      </div>

      {displayComment ? (
        <div className="mt-4 rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs font-semibold text-slate-700">
              Note <span className="ml-1 text-[11px] font-medium text-slate-500">({commentMode})</span>
            </div>

            <button
              type="button"
              onClick={() => setMode(commentMode === "easy" ? "detailed" : "easy")}
              className="text-xs font-semibold text-sky-700 hover:text-sky-800"
              title="Toggle note detail"
            >
              Switch to {commentMode === "easy" ? "detailed" : "easy"}
            </button>
          </div>

          <div className="mt-2 text-sm text-slate-700">
            <KeywordText text={displayComment} />
          </div>

          {easy && detailed && (
            <details className="mt-3 rounded-xl bg-slate-50 p-3">
              <summary className="cursor-pointer text-xs font-semibold text-slate-700">
                Show {commentMode === "easy" ? "detailed" : "easy"} version
              </summary>
              <div className="mt-2 text-sm text-slate-700">
                <KeywordText text={commentMode === "easy" ? detailed : easy} />
              </div>
            </details>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* --------------------------------- helpers -------------------------------- */
function flattenInventory(inv) {
  const wrap = (type, arr) => arr.map((x) => ({ ...x, type }));
  return [...wrap("consonant", inv.consonants), ...wrap("vowel", inv.vowels), ...wrap("diphthong", inv.diphthongs)];
}

/* ---------------------------------- page ---------------------------------- */
export default function SpeechSounds() {
  const [l1Code, setL1Code] = useState(L1_PACKS[0]?.meta?.code || "en");
  const [selectedIpa, setSelectedIpa] = useState(null);

  const l1Pack = useMemo(() => L1_PACKS.find((p) => p.meta.code === l1Code) || L1_PACKS[0], [l1Code]);

  const flat = useMemo(() => flattenInventory(NORWEGIAN_INVENTORY), []);
  const selected = useMemo(() => {
    if (!selectedIpa) return null;
    return flat.find((x) => x.ipa === selectedIpa) || null;
  }, [flat, selectedIpa]);

  const getOverlap = (ipa) => normalizeOverlap(l1Pack?.byIpa?.[ipa]?.overlap);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Speech sounds</h1>
          <p className="mt-1 text-sm text-slate-600">Click any sound to see the {l1Pack?.meta?.name} comparison.</p>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-700">L1</label>
          <select
            value={l1Code}
            onChange={(e) => {
              setL1Code(e.target.value);
              setSelectedIpa(null);
            }}
            className="rounded-lg border bg-white px-3 py-2 text-sm text-slate-700"
          >
            {L1_PACKS.map((p) => (
              <option key={p.meta.code} value={p.meta.code}>
                {p.meta.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4">
        <Legend />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Section title="Consonants">
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {NORWEGIAN_INVENTORY.consonants.map((s) => (
                <SoundTile
                  key={s.ipa}
                  ipa={s.ipa}
                  overlap={getOverlap(s.ipa)}
                  selected={selectedIpa === s.ipa}
                  onClick={() => setSelectedIpa(s.ipa)}
                />
              ))}
            </div>
          </Section>

          <Section title="Vowels">
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {NORWEGIAN_INVENTORY.vowels.map((s) => (
                <SoundTile
                  key={s.ipa}
                  ipa={s.ipa}
                  overlap={getOverlap(s.ipa)}
                  selected={selectedIpa === s.ipa}
                  onClick={() => setSelectedIpa(s.ipa)}
                />
              ))}
            </div>
          </Section>

          <Section title="Diphthongs">
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {NORWEGIAN_INVENTORY.diphthongs.map((s) => (
                <SoundTile
                  key={s.ipa}
                  ipa={s.ipa}
                  overlap={getOverlap(s.ipa)}
                  selected={selectedIpa === s.ipa}
                  onClick={() => setSelectedIpa(s.ipa)}
                />
              ))}
            </div>
          </Section>
        </div>

        <div className="lg:col-span-1">
          <Details selected={selected} l1Pack={l1Pack} />
        </div>
      </div>
    </div>
  );
}